// The signed-in person's logins (#466 § 5): every credential that reaches this
// developerz.ai user — the GitHub anchor (`id: 'github'`, never removable) and
// each linked SSO identity. Reads `GET /v1/me/identities`, removes through
// `DELETE /v1/me/identities/:id`, and LINKS by a real top-level form post to the
// API's `/auth/org/:slug/link` leg, never a `/v1` call: the IdP round-trip is a
// navigation, and the server binds the identity to the session's user — the
// client never names a subject (#1738 finding 1).

import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { API_BASE_URL, ApiError, apiFetch } from './api';
import { SESSION_TOKEN } from './session';

export const IDENTITIES_QUERY_KEY = 'me_identities';

export const IDENTITY_PROVIDERS = ['github', 'oidc', 'saml', 'platform_oidc'] as const;
export type IdentityProvider = (typeof IDENTITY_PROVIDERS)[number];

const IdentitySchema = z.object({
  id: z.string(),
  provider: z.enum(IDENTITY_PROVIDERS),
  accountLogin: z.string().nullable(),
  email: z.string().nullable(),
  linkedAt: z.string(),
  removable: z.boolean(),
});
export type Identity = z.infer<typeof IdentitySchema>;

const IdentitiesSchema = z.object({ data: z.array(IdentitySchema) });
/** `GET /v1/me/identities`'s body. */
export type IdentitiesPage = z.infer<typeof IdentitiesSchema>;

/** The person's logins. Idle with no session, like the other `/v1/me/*` reads. */
export function useIdentities(token: string | null = SESSION_TOKEN) {
  return createQuery(() => ({
    queryKey: [IDENTITIES_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch('/v1/me/identities', token, undefined, { schema: IdentitiesSchema }),
  }));
}

/** Remove one login: 204, or a 409 `last_credential` / 404 `ApiError`. */
export async function removeIdentity(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  await apiFetch<void>(`/v1/me/identities/${encodeURIComponent(id)}`, token, {
    method: 'DELETE',
  });
}

/**
 * The i18n key for a refused remove the page explains in its own words, or
 * `null` to show the API's `detail` (a 404 or a malformed-id 400):
 *   - 409 `last_credential`: the person's only way to sign in;
 *   - 409 `github_anchor`: the GitHub login anchors the account;
 *   - 403: this session (signed in through another org's SSO, or a token
 *     caller) may not unlink it.
 */
export function removeRefusalKey(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status === 403) return 'logins.remove_forbidden';
  if (err.status !== 409) return null;
  const code = err.extensions?.['code'];
  if (code === 'last_credential') return 'logins.last_credential';
  if (code === 'github_anchor') return 'logins.github_anchor';
  return null;
}

/** The `action` of the link form for an org's IdP slug — same origin as its login leg. */
export function identityLinkAction(slug: string): string {
  return `${API_BASE_URL}/auth/org/${encodeURIComponent(slug)}/link`;
}

/** The refusals the link callback names in `?link_error=`. */
export const LINK_ERROR_CODES = [
  'owned_elsewhere',
  'not_member',
  'idp_unreachable',
  'idp_misconfigured',
  'unauthorized',
  'sso_not_enabled',
  'saml_link_unsupported',
  'link_failed',
] as const;
export type LinkErrorCode = (typeof LINK_ERROR_CODES)[number];

/**
 * What the link callback came back with: `?linked=<slug>` or
 * `?link_error=<code>`. A code this bundle does not know is still a failure, so
 * it reads `unknown` rather than vanishing — the person tried to link and must
 * be told it did not happen.
 */
export type LinkOutcome =
  | { readonly kind: 'linked'; readonly slug: string }
  | { readonly kind: 'error'; readonly code: LinkErrorCode | 'unknown' };

export function linkOutcome(search: string): LinkOutcome | null {
  const params = new URLSearchParams(search);
  const error = params.get('link_error');
  if (error !== null) {
    const known = (LINK_ERROR_CODES as readonly string[]).includes(error);
    return { kind: 'error', code: known ? (error as LinkErrorCode) : 'unknown' };
  }
  const slug = params.get('linked');
  return slug ? { kind: 'linked', slug } : null;
}
